import type { Job, JobSpec } from "@/data/types";
import { bindSpec, getCanonicalSpecForArchetype, type SpecArchetype } from "@/data/jobSpec";

type BindableBomItem = { sku: string; name: string; quantity: number; cost?: number; price?: number };

function inferSpecArchetype(projectType?: string): SpecArchetype | undefined {
  const p = (projectType ?? "").toLowerCase();
  if (p.includes("tub to shower") || p.includes("tub-to-shower")) return "TTS-STD";
  if (p.includes("shower remodel")) return "SHWR-RPL";
  if (p.includes("shower replacement")) return "SHWR-RPL";
  if (p.includes("walk-in tub") || p.includes("walk in tub")) return "WALK-IN-TUB";
  if (p.includes("full bathroom") || p.includes("full bath")) return "FULL-BATH";
  if (p.includes("tub replacement")) return "TUB-RPL";
  if (p.includes("floor")) return "FLOORING";
  return undefined;
}

function positiveNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Derives a bound JobSpec for the given job, embedding the current
 * materialsList into the billOfMaterials of the appropriate archetype spec.
 *
 * A bound spec is only valid when the job has real material rows. Project type
 * alone must not synthesize a canonical BOM for imports or partial jobs.
 */
export function deriveBoundSpecForJob(job: Job): JobSpec | undefined {
  const materials = Array.isArray(job.materialsList) ? job.materialsList : [];
  const bom = materials
    .map((m) => {
      const sku = String((m as { sku?: string }).sku ?? "").trim();
      if (!sku) return null;
      const quantity = positiveNumber((m as { quantityRequired?: number }).quantityRequired);
      if (!quantity) return null;
      const name = String((m as { itemName?: string }).itemName ?? sku).trim();
      const unitCost = Number((m as { unitCost?: number }).unitCost ?? 0);
      const unitPrice = (m as { unitPrice?: number }).unitPrice;
      return {
        sku,
        name,
        quantity,
        cost: unitCost > 0 ? unitCost : undefined,
        price: unitPrice && unitPrice > 0 ? unitPrice : undefined,
      };
    })
    .filter((v): v is BindableBomItem => Boolean(v));

  if (bom.length === 0) return undefined;

  const archetype = inferSpecArchetype(job.projectType);
  const canonical = archetype ? getCanonicalSpecForArchetype(archetype) : undefined;
  if (!canonical) return undefined;

  const specToBind: JobSpec = {
    specId: canonical.specId,
    versionNumber: canonical.versionNumber,
    label: canonical.label,
    archetype: canonical.archetype,
    status: "canonical",
    phases: canonical.phases,
    billOfMaterials: bom,
  };
  return bindSpec(specToBind);
}
